/**
 * Airline ↔ Auth0 organization registry, per `docs/apis/auth-tds.md` §3.3 and
 * `docs/tenant-isolation-model.md` §4.4 / §6.1.
 *
 * **An Auth0 organization has two identifiers and they are not interchangeable.**
 * Conflating them is the trap `docs/tenant-isolation-model.md` §6.1 records: this file
 * used to carry one field named `orgId` that actually held the *name*, so wiring
 * `requireOrgId` from it would have rejected every token — a control that fails closed on
 * every request looks identical to a broken deployment.
 *
 *   - **name** — `org-latam`. Human-readable, chosen by us, lowercase alphanumeric + dashes
 *     (Auth0's organization-name validator rejects underscores). This is what
 *     `event.organization.name` yields in a post-login Action, and what the Auth0
 *     Management API's `getByName` takes.
 *   - **id** — `org_pCI16oO4mDkcDrsm`. Opaque, assigned by Auth0. This is the `org_id`
 *     **token claim**, what `/authorize?organization=` requires, and the only value
 *     `requireOrgId` may be given.
 *
 * The two are kept apart by construction: {@link Auth0OrgName} is `org-${string}` and
 * {@link Auth0OrgId} is `org_${string}`, which are disjoint template-literal types. Passing
 * one where the other is expected is a **compile error**, which is what `auth-tds.md` §3.3
 * asks for — *"a typo in the org name is a compile error, not a silent mis-tenanting."*
 *
 * The org ids below are the real dev-tenant values, cross-checked against
 * `apps/operator-ui/.env.example:8-9`, `infra/auth0/dev/tenant.yaml:75,130` and
 * `docs/apis/flight-predictor-auth-tds.md:385`. They are not invented.
 */

/** Auth0 organization **name** — `org-latam`. Never accepted by `requireOrgId`. */
export type Auth0OrgName = `org-${string}`;

/** Auth0 organization **id** — opaque `org_pCI16oO4mDkcDrsm`. The `org_id` token claim. */
export type Auth0OrgId = `org_${string}`;

/** Airline URN — `urn:airline:latam`. */
export type AirlineUrn = `urn:airline:${string}`;

export interface AirlineOrgEntry {
  readonly airlineUrn: AirlineUrn;
  /** Auth0 organization name (`org-latam`) — Management API `getByName`, Action lookups. */
  readonly orgName: Auth0OrgName;
  /** Auth0 organization id (`org_…`) — the `org_id` claim, and the only `requireOrgId` value. */
  readonly orgId: Auth0OrgId;
  readonly displayName: string;
}

export const AIRLINE_ORG_REGISTRY: readonly AirlineOrgEntry[] = [
  {
    airlineUrn: 'urn:airline:latam',
    orgName: 'org-latam',
    orgId: 'org_pCI16oO4mDkcDrsm',
    displayName: 'LATAM Airlines Group',
  },
  {
    airlineUrn: 'urn:airline:avianca',
    orgName: 'org-avianca',
    orgId: 'org_9V1PKHpel0nRxrOX',
    displayName: 'Avianca',
  },
  {
    airlineUrn: 'urn:airline:sky',
    orgName: 'org-sky',
    orgId: 'org_CMYLnl1N5bVgGNOM',
    displayName: 'SKY Airline',
  },
  {
    airlineUrn: 'urn:airline:etihad',
    orgName: 'org-etihad',
    orgId: 'org_Eebki5YHx8NDerbz',
    displayName: 'Etihad Airways',
  },
  {
    airlineUrn: 'urn:airline:southwest',
    orgName: 'org-southwest',
    orgId: 'org_TNd2nwBAdge6okTu',
    displayName: 'Southwest Airlines',
  },
  {
    airlineUrn: 'urn:airline:iberia',
    orgName: 'org-iberia',
    orgId: 'org_bpjdOst51lU5l6xs',
    displayName: 'Iberia',
  },
  {
    airlineUrn: 'urn:airline:plusultra',
    orgName: 'org-plusultra',
    orgId: 'org_mZjImFA8K6zMDW8O',
    displayName: 'Plus Ultra Líneas Aéreas',
  },
  {
    airlineUrn: 'urn:airline:nexa',
    orgName: 'org-nexa',
    orgId: 'org_qz6Y2s7lqbT0irOe',
    displayName: 'Nexa Studio (internal)',
  },
  {
    // Advanced Air (AN / WSN) — a Californian commuter operator. Its org is its
    // own, like every other airline's: the org is what carries the airline
    // claim, so a shared one would hand its operators another carrier's data.
    // What Advanced Air DOES share is the SPA client (it borrows
    // operator-ui-southwest-dev, the dev tenant being at its application cap)
    // and the standard five-account operator roster.
    airlineUrn: 'urn:airline:advancedair',
    orgName: 'org-advancedair',
    orgId: 'org_hSDbY3p07mnVfXeI',
    displayName: 'Advanced Air',
  },
  {
    // Air Mauritius (MK / MAU) — own org, shared SPA, standard roster: the same
    // arrangement as advancedair, for the same reason (application cap).
    airlineUrn: 'urn:airline:airmauritius',
    orgName: 'org-airmauritius',
    orgId: 'org_jM4IHb47JSzx8wJP',
    displayName: 'Air Mauritius',
  },
  {
    airlineUrn: 'urn:airline:flyyo',
    orgName: 'org-flyyo',
    orgId: 'org_Yoyu6pJHT9hKwwZL',
    displayName: 'Flyyo',
  },
];

const AIRLINE_URN_PREFIX = 'urn:airline:';

/** Raised when a deployment's tenant has no Auth0 organization in the registry. */
export class UnknownTenantOrgError extends Error {
  override readonly name = 'UnknownTenantOrgError';
}

const BY_ORG_NAME = new Map<string, AirlineOrgEntry>(
  AIRLINE_ORG_REGISTRY.map((e) => [e.orgName, e]),
);
const BY_ORG_ID = new Map<string, AirlineOrgEntry>(AIRLINE_ORG_REGISTRY.map((e) => [e.orgId, e]));
const BY_AIRLINE_URN = new Map<string, AirlineOrgEntry>(
  AIRLINE_ORG_REGISTRY.map((e) => [e.airlineUrn, e]),
);
// Tenant token is derived from the URN rather than stored, so there is exactly one place an
// airline's identity is written down and no way for the two to disagree.
const BY_TENANT = new Map<string, AirlineOrgEntry>(
  AIRLINE_ORG_REGISTRY.map((e) => [e.airlineUrn.slice(AIRLINE_URN_PREFIX.length), e]),
);

/** The tenant token for an entry — `urn:airline:latam` → `latam`. */
export function tenantOf(entry: AirlineOrgEntry): string {
  return entry.airlineUrn.slice(AIRLINE_URN_PREFIX.length);
}

// ── Lookups by tenant token ──────────────────────────────────────────────────────────────

export function lookupByTenant(tenant: string): AirlineOrgEntry | undefined {
  return BY_TENANT.get(tenant);
}

/** Auth0 org **id** for a tenant token — the value `requireOrgId` takes. */
export function orgIdForTenant(tenant: string): Auth0OrgId | undefined {
  return BY_TENANT.get(tenant)?.orgId;
}

/** Auth0 org **name** for a tenant token — the value the Management API's `getByName` takes. */
export function orgNameForTenant(tenant: string): Auth0OrgName | undefined {
  return BY_TENANT.get(tenant)?.orgName;
}

/**
 * The Auth0 org **id** this deployment must pin its tokens to — fails closed.
 *
 * This is the value `docs/tenant-isolation-model.md` §4.4 requires every BFF guard to pass
 * as `requireOrgId`. Compose it with the deployment's own tenant at the call site:
 *
 * ```ts
 * import { ownTenant } from '@nexa/messaging';
 * import { requiredOrgIdForTenant } from '@nexa/auth';
 *
 * const requireOrgId = requiredOrgIdForTenant(ownTenant());   // at boot, once
 * ```
 *
 * `@nexa/auth` deliberately does not import `@nexa/messaging` itself — identity primitives
 * are browser-safe and must not pull in the bus client. The composition belongs to the BFF.
 *
 * @throws {UnknownTenantOrgError} when the tenant has no organization. A deployment whose
 * tenant is unknown to the registry must not start: silently skipping the pin would restore
 * exactly the §4.4 hole where any airline's token is accepted.
 */
export function requiredOrgIdForTenant(tenant: string): Auth0OrgId {
  const orgId = orgIdForTenant(tenant);
  if (!orgId) {
    throw new UnknownTenantOrgError(
      `[@nexa/auth] tenant '${tenant}' has no Auth0 organization in AIRLINE_ORG_REGISTRY, ` +
        `so its tokens cannot be pinned (docs/tenant-isolation-model.md §4.4). Add the ` +
        `airline to packages/auth/src/airline-org-map.ts and the post-login Action.`,
    );
  }
  return orgId;
}

// ── Lookups by airline URN ───────────────────────────────────────────────────────────────

export function lookupByAirlineUrn(airlineUrn: string): AirlineOrgEntry | undefined {
  return BY_AIRLINE_URN.get(airlineUrn);
}

export function orgIdForAirlineUrn(airlineUrn: string): Auth0OrgId | undefined {
  return BY_AIRLINE_URN.get(airlineUrn)?.orgId;
}

export function orgNameForAirlineUrn(airlineUrn: string): Auth0OrgName | undefined {
  return BY_AIRLINE_URN.get(airlineUrn)?.orgName;
}

// ── Lookups by organization ──────────────────────────────────────────────────────────────

/** Looks up by opaque org **id** (`org_…`) — use for anything derived from a token claim. */
export function lookupByOrgId(orgId: string): AirlineOrgEntry | undefined {
  return BY_ORG_ID.get(orgId);
}

/** Looks up by org **name** (`org-…`) — use for anything derived from an Auth0 Action. */
export function lookupByOrgName(orgName: string): AirlineOrgEntry | undefined {
  return BY_ORG_NAME.get(orgName);
}

export function airlineUrnForOrgId(orgId: string): AirlineUrn | undefined {
  return BY_ORG_ID.get(orgId)?.airlineUrn;
}

export function airlineUrnForOrgName(orgName: string): AirlineUrn | undefined {
  return BY_ORG_NAME.get(orgName)?.airlineUrn;
}

// ── Legacy accessors ─────────────────────────────────────────────────────────────────────

/**
 * @deprecated Ambiguous: "org" here may be a name or an id. Resolves either form, so it is
 * safe, but prefer {@link airlineUrnForOrgId} for token-derived values and
 * {@link airlineUrnForOrgName} for Action-derived ones.
 */
export function mapOrgToAirlineUrn(org: string): AirlineUrn | undefined {
  return BY_ORG_ID.get(org)?.airlineUrn ?? BY_ORG_NAME.get(org)?.airlineUrn;
}

/**
 * Auth0 org **name** for an airline URN.
 *
 * @deprecated The name does not say which identifier it returns. Use
 * {@link orgNameForAirlineUrn} (same value) or {@link orgIdForAirlineUrn} (the token claim).
 */
export function mapAirlineUrnToOrg(airlineUrn: string): Auth0OrgName | undefined {
  return BY_AIRLINE_URN.get(airlineUrn)?.orgName;
}

/**
 * @deprecated Ambiguous. Resolves either identifier form; prefer {@link lookupByOrgId} or
 * {@link lookupByOrgName}.
 */
export function lookupByOrg(org: string): AirlineOrgEntry | undefined {
  return BY_ORG_ID.get(org) ?? BY_ORG_NAME.get(org);
}
